import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Mail, MapPin, Phone, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useHiringApi, errMsg } from './useHiringApi';
import { ALL_STAGES, fmtDate, fullName, stageMeta } from './hiringConstants';

const NONE = '__none';

export const Panel = ({ title, children, action, testId }) => (
  <section className="bg-white border border-zinc-200 rounded-xl" data-testid={testId}>
    <header className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
      <h2 className="text-sm font-bold text-zinc-900">{title}</h2>
      {action}
    </header>
    <div className="p-4 space-y-3">{children}</div>
  </section>
);

export const Status = ({ s }) => {
  if (!s) return <span className="text-zinc-400">—</span>;
  const tone = s.status === 'sent' || s.status === 'created' ? 'text-emerald-700' : s.status === 'failed' ? 'text-red-700' : 'text-amber-700';
  return <span className={tone} title={s.error || ''}>{s.status.replace('_', ' ')}{s.error ? ` — ${s.error}` : ''}</span>;
};

export default function ApplicantHeader({ a, cfg, onUpdated, onConvert }) {
  const api = useHiringApi();
  const [nextText, setNextText] = useState(a.next_action?.text || '');
  const [nextDue, setNextDue] = useState(a.next_action?.due || '');
  const m = stageMeta(a.stage);

  const move = async (stage) => {
    if (stage === a.stage) return;
    let reason;
    if (['rejected', 'withdrawn', 'talent_pool'].includes(stage)) {
      reason = window.prompt(`Reason for moving to ${stageMeta(stage).label} (saved to the activity log):`) ?? null;
      if (reason === null) return;
    }
    try { onUpdated(await api.stage(a.id, stage, reason)); toast.success(`Moved to ${stageMeta(stage).label}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setOwner = async (owner_id) => {
    try { onUpdated(await api.assign(a.id, { owner_id: owner_id === NONE ? null : owner_id })); } catch (e) { toast.error(errMsg(e)); }
  };
  const saveNext = async () => {
    try { onUpdated(await api.assign(a.id, { next_action: nextText, next_action_due: nextDue || null })); toast.success('Next action saved'); } catch (e) { toast.error(errMsg(e)); }
  };
  const downloadResume = async () => {
    try {
      const blob = await api.resumeBlob(a.id);
      const url = URL.createObjectURL(blob);
      const link = Object.assign(document.createElement('a'), { href: url, download: a.resume.filename });
      link.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e, 'Résumé could not be downloaded')); }
  };

  return (
    <header className="bg-white border-b border-zinc-200" data-testid="applicant-header">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-4">
        <div className="flex flex-wrap items-start gap-4">
          <Link to="/admin/hiring" className="text-zinc-500 hover:text-zinc-900 mt-1" data-testid="back-to-hiring"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-bold text-zinc-900" data-testid="applicant-name">{fullName(a)}</h1>
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${m.tone}`} data-testid="applicant-stage-badge">{m.label}</span>
              {a.is_repeat && <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="repeat-badge">Repeat applicant</span>}
              {a.converted_guru_user_id && <Link to={`/admin/cleaners`} className="text-[11px] font-semibold px-2 py-0.5 rounded bg-green-100 text-green-800" data-testid="converted-badge">Guru account created</Link>}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-zinc-600">
              {a.email && <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1 hover:text-orange-700"><Mail className="w-3 h-3" />{a.email}</a>}
              {a.phone && <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1 hover:text-orange-700"><Phone className="w-3 h-3" />{a.phone}</a>}
              {(a.city || a.zip) && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[a.city, a.zip].filter(Boolean).join(' ')}</span>}
              <span>Applied {fmtDate(a.applied_at)} · {a.source_channel === 'manual' ? `Added manually (${a.inquiry_channel || 'inquiry'})` : 'Website'} · Source: {a.recruiting_source || '—'}{a.referrer ? ` (${a.referrer})` : ''}</span>
            </div>
            {a.prior_applications?.length > 0 && (
              <p className="text-xs text-amber-700 mt-1" data-testid="prior-applications">
                Earlier applications: {a.prior_applications.map((p) => <Link key={p.id} to={`/admin/hiring/${p.id}`} className="underline mr-2">{fmtDate(p.applied_at)} ({stageMeta(p.stage).label})</Link>)}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {a.resume && <Button variant="outline" size="sm" onClick={downloadResume} data-testid="download-resume"><Download className="w-4 h-4 mr-1" /> Résumé</Button>}
            {a.stage === 'hired' && !a.converted_guru_user_id && <Button size="sm" className="bg-green-700 hover:bg-green-800" onClick={onConvert} data-testid="convert-to-guru-btn"><UserPlus className="w-4 h-4 mr-1" /> Create Guru account</Button>}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="text-xs font-semibold text-zinc-600 space-y-1"><span>Stage</span>
            <Select value={a.stage} onValueChange={move}>
              <SelectTrigger data-testid="stage-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ALL_STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </label>
          <label className="text-xs font-semibold text-zinc-600 space-y-1"><span>Hiring owner</span>
            <Select value={a.owner_id || NONE} onValueChange={setOwner}>
              <SelectTrigger data-testid="owner-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={NONE}>Unassigned</SelectItem>{(cfg?.staff || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </label>
          <div className="text-xs font-semibold text-zinc-600 space-y-1"><span>Next action</span>
            <div className="flex gap-2">
              <Input value={nextText} onChange={(e) => setNextText(e.target.value)} placeholder="e.g. Call to schedule interview" data-testid="next-action-input" />
              <Input type="date" value={nextDue} onChange={(e) => setNextDue(e.target.value)} className="w-[150px]" data-testid="next-action-due" />
              <Button size="sm" variant="outline" onClick={saveNext} data-testid="next-action-save">Save</Button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
